import { useStore } from '@tanstack/react-store'
import { Store } from '@tanstack/store'

import * as R from 'core/helpers/remeda'

import makeStorage from '../storage/makeStorage'
// Assuming usePeachGlobal and useStableCallback are not directly needed for store logic
// but hooks using this store might use them.

// Define Standard Environment Configuration
export type StandardEnvConfig = {
  id: string // Unique identifier (e.g., 'production', 'sandbox')
  label: string // User-facing name (e.g., "Production")
  apiBase: string
  companyId: string // Base companyId specific to this standard environment
  userType?: string // Optional: if we decide to default userType
  // defaultAuthMethod is implicitly 'OneTimeCodeEmail' for standard envs for now
}

export const STANDARD_ENVIRONMENTS: Array<StandardEnvConfig> = [
  {
    id: 'production',
    label: 'Production',
    apiBase: import.meta.env.VITE_PRODUCTION_API_BASE as string,
    companyId: import.meta.env.VITE_PRODUCTION_COMPANY_ID as string,
    userType: 'agent', // Example default
  },
  {
    id: 'sandbox',
    label: 'Sandbox',
    apiBase: import.meta.env.VITE_SANDBOX_API_BASE as string,
    companyId: import.meta.env.VITE_SANDBOX_COMPANY_ID as string,
    userType: 'agent', // Example default
  },
  {
    id: 'development',
    label: 'Development',
    apiBase: import.meta.env.VITE_DEVELOPMENT_API_BASE as string,
    companyId: import.meta.env.VITE_DEVELOPMENT_COMPANY_ID as string,
    userType: 'agent', // Example default
  },
  // Add other standard environments as needed
]

// Export envStorage for components that need it directly
export const envStorage = makeStorage('peach-env')

// Helper functions (migrated from useEnv.tsx)
const parseOrigin = (apiBase: string | undefined): string | undefined => {
  if (!apiBase) return undefined
  try {
    return apiBase.split('//')[1].split('/')[0]
  } catch {
    return apiBase
  }
}

const getNewEnvKey = (
  obj: { key?: string; apiBase?: string },
  envs: Array<Env>,
): string => {
  const { key, apiBase } = obj

  const isUnique = (newKey: string | undefined): boolean => {
    if (!newKey) return false
    return envs.every(
      ({ id, key: envKey }) => newKey !== id && newKey !== envKey,
    )
  }

  if (key && isUnique(key)) return key

  const newKeyFromApiBase = parseOrigin(apiBase)

  let safeKey: string | undefined = newKeyFromApiBase || 'new-env'
  if (!isUnique(safeKey)) {
    let count = 1
    let tempKey = `${safeKey}--${count}`
    while (!isUnique(tempKey) && count < 20) {
      count += 1
      tempKey = `${safeKey}--${count}`
    }
    safeKey = tempKey
  }

  if (!isUnique(safeKey)) {
    // Fallback if everything else fails to produce a unique key
    safeKey = `env-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`
  }

  return safeKey // At this point, safeKey should be a valid, unique string
}

const parseEnvs = (envs: Array<any> = []): Array<Env> => {
  const newEnvs: Array<Env> = []
  envs.forEach((env) => {
    const { key, ...rest } = env || {}
    const newEnv = key
      ? env
      : { ...rest, key: getNewEnvKey(env, [...envs, ...newEnvs]) }
    // Ensure id is present, defaulting to key if not
    if (!newEnv.id) {
      newEnv.id = newEnv.key
    }
    newEnvs.push(newEnv)
  })
  return newEnvs
}

const makeEnv = (envData: Partial<EnvData>, existingEnvs: Array<Env>): Env => {
  const {
    label,
    apiBase,
    companyId,
    userType,
    username,
    key: providedKey,
  } = envData
  const key =
    providedKey || getNewEnvKey({ key: providedKey, apiBase }, existingEnvs)

  return {
    id: key, // Use the generated or provided key as the ID
    key,
    label: label || parseOrigin(apiBase) || 'New Environment',
    apiBase: apiBase || '',
    companyId: companyId || '',
    userType: userType || '',
    username: username || '',
  }
}

// Types
export type EnvData = {
  label?: string
  apiBase?: string
  companyId?: string
  userType?: string
  username?: string
  key?: string
  id?: string
}

export type Env = {
  id: string
  key: string
  apiBase: string
  isStandard?: boolean // Flag to identify standard environments
} & EnvData

export type EnvStoreState = {
  customEnvs: Array<Env> // Renamed from 'envs' to be explicit
  activeEnvRef:
    | { type: 'standard'; id: string }
    | { type: 'custom'; id: string }
    | undefined
}

// --- Migration and Initial State Setup ---

// 1. Custom Environments List Migration
let loadedCustomEnvsFromStorage = envStorage.get('customEnvs')
const oldEnvsFromStorage = envStorage.get('envs') // Try to load from old key

if (
  (!loadedCustomEnvsFromStorage || loadedCustomEnvsFromStorage.length === 0) &&
  oldEnvsFromStorage &&
  oldEnvsFromStorage.length > 0
) {
  console.info('Migrating custom environments from old storage key "envs".')
  loadedCustomEnvsFromStorage = oldEnvsFromStorage
  envStorage.set('customEnvs', loadedCustomEnvsFromStorage) // Save to new key
  envStorage.set('envs', null) // Remove old key
}

const initialCustomEnvs = parseEnvs(loadedCustomEnvsFromStorage || [])

// 2. Active Environment Reference Migration & Determination
const getInitialActiveEnvRef = (): EnvStoreState['activeEnvRef'] => {
  let activeReference = envStorage.get('activeEnvRef')

  if (activeReference) {
    // Validate existing activeEnvRef
    if (
      activeReference.type === 'standard' &&
      STANDARD_ENVIRONMENTS.find((e) => e.id === activeReference.id)
    ) {
      return activeReference
    }
    if (
      activeReference.type === 'custom' &&
      initialCustomEnvs.find((e) => e.id === activeReference.id)
    ) {
      return activeReference
    }
    // Invalid stored activeEnvRef, clear it
    envStorage.set('activeEnvRef', null)
    activeReference = undefined // Proceed to migration or default
  }

  // If no valid new activeEnvRef, try migrating from old activeId
  const oldActiveId = envStorage.get('activeId')
  if (oldActiveId) {
    console.info(
      'Attempting to migrate active environment from old storage key "activeId".',
    )
    const matchingCustomEnv = initialCustomEnvs.find(
      (e) => e.id === oldActiveId,
    )
    if (matchingCustomEnv) {
      activeReference = { type: 'custom' as const, id: oldActiveId }
      envStorage.set('activeEnvRef', activeReference) // Save migrated ref
      envStorage.set('activeId', null) // Remove old activeId key
      console.info(
        'Successfully migrated activeId to activeEnvRef.',
        activeReference,
      )
      return activeReference
    }
    // Old activeId found but doesn't match any known custom env, remove it.
    envStorage.set('activeId', null)
  }

  // Default to the first standard environment if no valid/migrated ref
  if (STANDARD_ENVIRONMENTS.length > 0) {
    return { type: 'standard' as const, id: STANDARD_ENVIRONMENTS[0].id }
  }
  // Fallback to first custom env if no standard envs
  if (initialCustomEnvs.length > 0) {
    return { type: 'custom' as const, id: initialCustomEnvs[0].id }
  }
  return undefined
}

const initialActiveRef = getInitialActiveEnvRef()

const initialState: EnvStoreState = {
  customEnvs: initialCustomEnvs,
  activeEnvRef: initialActiveRef,
}

// Store instance
export const envStore = new Store<EnvStoreState>(initialState)

// Actions
export const setActiveId = (id: string | undefined) => {
  // This function now needs to know if the ID refers to a standard or custom env.
  // It's better to use selectStandardEnvironment or ensure a custom env with this ID exists.
  // For now, let's assume it's for custom environments or it's called by a context-aware function.
  // @ts-ignore
  envStore.setState((state) => {
    const customEnvExists = state.customEnvs.find((e) => e.id === id)
    if (customEnvExists) {
      const newActiveRef = { type: 'custom' as const, id }
      envStorage.set('activeEnvRef', newActiveRef)
      return { ...state, activeEnvRef: newActiveRef }
    }
    // If trying to set an ID that doesn't match a custom env, and it's not a standard one,
    // we might fall back or log an error. For now, retain current behavior if ID is not found.
    // Or, default to the first available if ID is not found among custom envs
    const fallbackRef =
      state.activeEnvRef ?? // keep current if id is invalid
      (STANDARD_ENVIRONMENTS.length > 0
        ? { type: 'standard' as const, id: STANDARD_ENVIRONMENTS[0].id }
        : state.customEnvs.length > 0
        ? { type: 'custom' as const, id: state.customEnvs[0].id }
        : undefined)
    if (
      !customEnvExists &&
      !STANDARD_ENVIRONMENTS.find((stdEnv) => stdEnv.id === id)
    ) {
      envStorage.set('activeEnvRef', fallbackRef)
      return { ...state, activeEnvRef: fallbackRef }
    }
    // If id matches a standard env, selectStandardEnvironment should be used.
    // This direct setActiveId might be deprecated or used internally with caution.
    return state
  })
}

export const selectStandardEnvironment = (standardEnvId: string) => {
  envStore.setState((state) => {
    if (STANDARD_ENVIRONMENTS.find((e) => e.id === standardEnvId)) {
      const newActiveRef = { type: 'standard' as const, id: standardEnvId }
      envStorage.set('activeEnvRef', newActiveRef)
      return { ...state, activeEnvRef: newActiveRef }
    }
    return state // Invalid standardEnvId
  })
}

export const addEnv = (envData: Partial<EnvData>): Env => {
  let newEnvInstance: Env | undefined

  envStore.setState((state) => {
    const createdEnv = makeEnv(envData, state.customEnvs)

    // The makeEnv function is expected to always return an Env with a defined string id.
    // If createdEnv.id is not a string, it indicates a problem with makeEnv or Env types.
    if (typeof createdEnv.id !== 'string') {
      console.error(
        'Critical: makeEnv did not produce an environment with a valid string ID.',
        createdEnv,
      )
      // Persist newEnvInstance as undefined so the outer function can handle it
      newEnvInstance = undefined
      return state // Return current state to avoid corruption
    }

    // Assign to the outer variable for returning from addEnv
    newEnvInstance = createdEnv

    const oldList = state.customEnvs.filter((e) => e.id !== newEnvInstance!.id)
    const newCustomEnvs = [...oldList, newEnvInstance]
    envStorage.set('customEnvs', newCustomEnvs)

    // Now, newEnvInstance.id is confirmed to be a string.
    const newActiveRef = { type: 'custom' as const, id: newEnvInstance.id }
    envStorage.set('activeEnvRef', newActiveRef)

    return { ...state, customEnvs: newCustomEnvs, activeEnvRef: newActiveRef }
  })

  if (!newEnvInstance) {
    // This implies the makeEnv or subsequent logic failed, and state was not updated as expected.
    throw new Error(
      'Environment creation failed within setState, newEnvInstance is undefined.',
    )
  }
  return newEnvInstance
}

export const editEnv = (id: string, envData: Partial<EnvData>) => {
  envStore.setState((state) => {
    const list = [...state.customEnvs]
    const envIndex = list.findIndex((e) => e.id === id)
    if (envIndex === -1) return state // Custom Env not found

    const existingEnv = list[envIndex]
    // Preserve original key and id if not explicitly changed
    const updatedEnvData = {
      ...existingEnv,
      ...envData,
      id: existingEnv.id, // id should not change during edit
      key:
        envData.key && envData.key !== existingEnv.key
          ? getNewEnvKey(
              { ...existingEnv, ...envData },
              state.customEnvs.filter((e) => e.id !== id),
            )
          : existingEnv.key,
    }
    list[envIndex] = updatedEnvData

    envStorage.set('customEnvs', list)
    return { ...state, customEnvs: list }
  })
}

export const removeEnvId = (idToRemove: string) => {
  envStore.setState((state) => {
    const envToRemove = state.customEnvs.find((e) => e.id === idToRemove)
    let customEnvsChanged = false
    let activeRefChanged = false

    if (!envToRemove) {
      // If the environment to remove doesn't exist, no changes should occur.
      // However, the original code proceeded to potentially re-save customEnvs and activeEnvRef.
      // We should return the state early if no env is found to remove.
      return state
    }

    // Proceed with removal if envToRemove is found
    const individualEnvStorage = makeStorage(envToRemove.key)
    individualEnvStorage.clear() // Clear the specific environment's storage

    const newCustomEnvs = state.customEnvs.filter((e) => e.id !== idToRemove)
    if (!R.isDeepEqual(newCustomEnvs, state.customEnvs)) {
      envStorage.set('customEnvs', newCustomEnvs)
      customEnvsChanged = true
    }

    let newActiveRef = state.activeEnvRef
    if (
      state.activeEnvRef?.type === 'custom' &&
      state.activeEnvRef.id === idToRemove
    ) {
      // If the removed env was active, try to activate another one
      if (STANDARD_ENVIRONMENTS.length > 0) {
        newActiveRef = { type: 'standard', id: STANDARD_ENVIRONMENTS[0].id }
      } else if (newCustomEnvs.length > 0) {
        newActiveRef = { type: 'custom', id: newCustomEnvs[0].id }
      } else {
        newActiveRef = undefined
      }
      // Only set if activeEnvRef actually changed
      if (!R.isDeepEqual(newActiveRef, state.activeEnvRef)) {
        envStorage.set('activeEnvRef', newActiveRef)
        activeRefChanged = true
      }
    }
    // Only return a new state object if something actually changed in the store's direct state
    if (customEnvsChanged || activeRefChanged) {
      return { ...state, customEnvs: newCustomEnvs, activeEnvRef: newActiveRef }
    }
    return state // No change to store state if only deeper storage was affected or no actual change
  })
}

// Selectors (custom hooks)
export const useCustomEnvs = () =>
  useStore(envStore, (state) => state.customEnvs) // Renamed
export const useActiveEnvRef = () =>
  useStore(envStore, (state) => state.activeEnvRef)

export const useActiveEnv = (): Env | undefined =>
  useStore(envStore, (state) => {
    if (!state.activeEnvRef) return undefined

    if (state.activeEnvRef.type === 'standard') {
      const standardEnv = STANDARD_ENVIRONMENTS.find(
        (e) => e.id === state.activeEnvRef!.id,
      )
      if (standardEnv) {
        return {
          ...standardEnv, // Includes id, label, apiBase, companyId, userType
          key: standardEnv.id, // Use id as key for standard envs
          isStandard: true,
        }
      }
    } else if (state.activeEnvRef.type === 'custom') {
      const customEnv = state.customEnvs.find(
        (e) => e.id === state.activeEnvRef!.id,
      )
      if (customEnv) {
        return { ...customEnv, isStandard: false }
      }
    }
    return undefined
  })

export const useActiveKey = (): string | undefined => {
  const activeEnv = useActiveEnv() // Call useActiveEnv at the top level
  return activeEnv?.key
}

// Combines all envs for display purposes if needed, standard ones first
export const useAllEnvsForDisplay = (): Array<Env> =>
  useStore(envStore, (state) => {
    const standardDisplayEnvs = STANDARD_ENVIRONMENTS.map((sEnv) => ({
      ...sEnv,
      key: sEnv.id,
      isStandard: true,
    }))
    return [
      ...standardDisplayEnvs,
      ...state.customEnvs.map((cEnv) => ({ ...cEnv, isStandard: false })),
    ]
  })

export const useEnvApi = () => ({
  customEnvs: useCustomEnvs(), // Renamed
  activeEnvRef: useActiveEnvRef(),
  activeEnv: useActiveEnv(),
  activeKey: useActiveKey(),
  allEnvsForDisplay: useAllEnvsForDisplay(), // New selector
  selectStandardEnvironment, // New action
  setActiveId, // Correctly referring to the renamed function
  addEnv,
  editEnv,
  removeEnvId,
})

// This function might be useful for components that need the raw storage instance for the active env
export const useActiveEnvStorage = () => {
  const activeKey = useActiveKey()
  // Memoize the storage instance based on activeKey
  // This is a simplified version; true memoization might need React.useMemo if used in a component
  if (activeKey) {
    return makeStorage(activeKey)
  }
  return undefined // Or a default/dummy storage
}

// To ensure Peach Global context is updated if still needed elsewhere.
// This would typically be done from a React component/hook that uses the store.
// For example, in a top-level provider or the modified useEnvManagement.
// import { usePeachGlobal } from 'peach/hooks';
// export const useSyncEnvStoreToPeachGlobal = () => {
//   const envApi = useEnvApi();
//   const activeEnv = useActiveEnv();
//   usePeachGlobal('envStorage', envStorage); // The main 'peach-env' storage
//   usePeachGlobal('appStorage', useActiveEnvStorage()); // Storage for the current active env
//   usePeachGlobal('envApi', envApi); // The API to interact with envs
//   usePeachGlobal('env', activeEnv); // The current active env object
// };

console.info('env.ts loaded and envStore initialized:', envStore.state)
